import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fromJS } from "immutable";
import { Switch } from "@headlessui/react";
import { LockOpen, Lock } from "lucide-react";
import LayoutAction from "../actions/LayoutAction";
import { Button, Modal } from "../components/ui";
import TaskAction from "../actions/TaskAction";
import InputDebounce from "../components/InputDebounce";
import FilterAction from "../actions/FilterAction";
import StarAlertIcon from "../icons/StarAlertIcon";
import { useQuery } from "../AppLayout";
import { getIconByView } from "../main/component/ViewMode";

const ViewModeDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const currentParams = useQuery();

  const projectView = currentParams.get("view") || "GRID";
  const contentType = currentParams.get("type") || "SALE";
  const appView = currentParams.get("key") || "";

  const filters = useSelector((state: any) => state.taskReducer.get("filters")) || fromJS({});
  const sorts = useSelector((state: any) => state.taskReducer.get("sorts")) || fromJS([]);
  const groupBy = useSelector((state: any) => state.taskReducer.get("groupBy"));
  const groupByVisibleFieldIds =
    useSelector((state: any) => state.taskReducer.get("groupByVisibleFieldIds")) || fromJS([]);
  const boardCardFields =
    useSelector((state: any) => state.taskReducer.get("boardCardFields")) || fromJS([]);
  const showCardLabels = useSelector((state: any) => state.taskReducer.get("showCardLabels"));

  const show = useSelector((state: any) => state.taskReducer.getIn(["showViewModeDialog", "show"]));
  const myFilter =
    useSelector((state: any) => state.taskReducer.getIn(["showViewModeDialog", "myFilter"])) ||
    fromJS({
      name: "",
      visibility: "Private",
    });
  const viewName = myFilter.get("name");
  const viewVisibility = myFilter.get("visibility");

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleSave = () => {
    const errors: Record<string, string> = {};
    if (!viewName) {
      errors.viewName = t("Title is required");
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    let requestData = {
      name: viewName,
      data: {
        ...(myFilter.get("data")?.toJS() || {}),
        filters: filters.toJS(),
        sorts: sorts.toJS(),
        groupBy,
        groupByVisibleFieldIds: groupByVisibleFieldIds.toJS(),
        boardCardFields: boardCardFields.toJS(),
        showCardLabels,
        types: [contentType],
        view: projectView,
      },
      visibility: viewVisibility,
      projectId,
    };
    if (myFilter.get("id")) {
      requestData = {
        ...myFilter.toJS(),
      };
      requestData.name = viewName;
      requestData.visibility = viewVisibility;
    }
    dispatch(FilterAction.saveFilter(requestData)).then(({ payload }) => {
      dispatch(FilterAction.getMyFilters(projectId, contentType));
      handleClose();
      dispatch(
        LayoutAction.setNotification({
          title: t("View Settings"),
          type: "success",
          message: t("Settings updated!"),
        })
      );
      if (!myFilter.get("id") && payload && payload.id) {
        navigate(
          `/workspaces/${projectId}?id=${payload.id}&view=${
            payload.data.view || "GRID"
          }&type=${contentType}&key=${appView}`
        );
      }
    });
  };

  const handleClose = () => {
    setValidationErrors({});
    dispatch(TaskAction.hideViewModeDialog());
  };

  const modalContent = (
    <div className="p-4">
      <div className="flex justify-center pb-3">
        <StarAlertIcon />
      </div>
      <div className="text-center px-4 pb-3 text-xl font-bold">
        {myFilter.get("id") ? t("Edit view") : t("Save New View")}
      </div>
      {myFilter.get("id") ? null : (
        <div className="pb-3 text-gray-500">
          {t("Save current view setting, sorting, and filters as new view")}
        </div>
      )}
      <div className="mt-2 flex items-center justify-center">
        <div className="h-9 flex items-center rounded-sm px-3 bg-gray-50 border border-gray-300 rounded-e-none">
          {getIconByView(myFilter.get("id") ? myFilter.getIn(["data", "view"]) : projectView)}
        </div>
        <InputDebounce
          autoFocus
          className="h-9 rounded-sm px-3 text-base sm:text-sm/6 text-gray-900 placeholder:text-gray-400 border border-gray-300 focus:outline-none border-l-0 rounded-s-none"
          placeholder={t("Enter a title for this view")}
          value={viewName}
          onChange={(value: string) => {
            dispatch(TaskAction.changeViewModeData("name", value));
          }}
        />
      </div>
      {validationErrors.viewName && (
        <p className="text-center text-xs text-red-600 mt-1">{validationErrors.viewName}</p>
      )}
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-semibold">{t("Visibility")}</span>
        <Switch
          checked={viewVisibility === "Shared"}
          onChange={(checked) => {
            if (checked) {
              dispatch(TaskAction.changeViewModeData("visibility", "Shared"));
            } else {
              dispatch(TaskAction.changeViewModeData("visibility", "Private"));
            }
          }}
          className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-red-600 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-green-600"
        >
          <Lock
            className={`absolute z-1 top-1 left-1 h-3 w-3 ${
              viewVisibility === "Shared" ? "text-white" : "text-red-600"
            }`}
          />
          <LockOpen
            className={`absolute z-1 top-1 right-1 h-3 w-3 ${
              viewVisibility === "Shared" ? "text-green-600" : "text-white"
            }`}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
          />
        </Switch>
        <span className="text-gray-500 text-sm font-semibold">
          {viewVisibility === "Shared" ? t("Shared") : t("Private")}
        </span>
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button size="sm" variant="secondary" onClick={handleClose}>
          {t("Cancel")}
        </Button>
        <Button size="sm" variant="primary" onClick={handleSave}>
          {t("Confirm")}
        </Button>
      </div>
    </div>
  );

  if (!show) return null;

  return (
    <Modal
      isOpen={show}
      onClose={handleClose}
      title=""
      showHeader={false}
      className="w-auto min-w-md max-h-[98vh]"
      footer={modalFooter}
    >
      {modalContent}
    </Modal>
  );
};

export default ViewModeDialog;
